import {
  addWeeks,
  eachDayOfInterval,
  endOfISOWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  isWeekend,
  parseISO,
  setISOWeek,
  setISOWeekYear,
  startOfISOWeek,
} from 'date-fns'
import { nl } from 'date-fns/locale'

export type DayStatus = 'over' | 'ok' | 'low' | 'empty' | 'none'

export interface DayMeta {
  date: Date
  iso: string
  dayIndex: number
  isWorkday: boolean
  short: string
  label: string
}

export interface WeekMeta {
  id: string
  isoWeek: number
  year: number
  start: Date
  end: Date
  days: DayMeta[]
  label: string
  rangeLabel: string
  /** Zonder jaartal en met afgekorte maand; voor plekken waar de balk krap is. */
  rangeShort: string
}

const shortLabels = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']

export function isoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function buildDays(start: Date, end: Date): DayMeta[] {
  return eachDayOfInterval({ start, end }).map((date, dayIndex) => ({
    date,
    iso: isoDate(date),
    dayIndex,
    isWorkday: !isWeekend(date),
    short: shortLabels[dayIndex],
    label: format(date, 'EEEE d MMMM', { locale: nl }),
  }))
}

function metaFrom(start: Date): WeekMeta {
  const end = endOfISOWeek(start)
  const isoWeek = getISOWeek(start)
  const year = getISOWeekYear(start)
  return {
    id: `${year}-W${String(isoWeek).padStart(2, '0')}`,
    isoWeek,
    year,
    start,
    end,
    days: buildDays(start, end),
    label: `Week ${isoWeek}`,
    rangeLabel: `${format(start, 'd', { locale: nl })} t/m ${format(end, 'd MMMM yyyy', { locale: nl })}`,
    rangeShort: `${format(start, 'd MMM', { locale: nl })} t/m ${format(end, 'd MMM', { locale: nl })}`,
  }
}

export function weekMetaFromDate(reference: Date): WeekMeta {
  return metaFrom(startOfISOWeek(reference))
}

export function weekMetaFromId(id: string): WeekMeta {
  const match = /^(\d{4})-W(\d{1,2})$/.exec(id)
  if (!match) return weekMetaFromDate(new Date())
  const year = Number(match[1])
  const week = Number(match[2])
  let anchor = setISOWeekYear(new Date(year, 5, 1), year)
  anchor = setISOWeek(anchor, week)
  return metaFrom(startOfISOWeek(anchor))
}

export function isoWeekId(reference: Date): string {
  return weekMetaFromDate(reference).id
}

export function shiftWeekId(id: string, delta: number): string {
  const meta = weekMetaFromId(id)
  return isoWeekId(addWeeks(meta.start, delta))
}

export function dayStatus(hours: number, isWorkday: boolean): DayStatus {
  if (hours > 12) return 'over'
  if (hours >= 6) return 'ok'
  if (hours > 0) return 'low'
  return isWorkday ? 'empty' : 'none'
}

/** Korte dagaanduiding voor lopende tekst en knoplabels: "31 aug". */
export function formatDayLabel(iso: string): string {
  return format(parseISO(iso), 'd MMM', { locale: nl })
}

/** De datum van vandaag in lokale tijd; toISOString() zou de UTC-dag geven. */
export function todayIsoDate(): string {
  return isoDate(new Date())
}
